import { SendPlusFill, ThreeDotsVertical } from "react-bootstrap-icons";
import TableHeader from "../Components/TableHeader";
import OutsideClickHandler from "react-outside-click-handler";
import TableLoader from "../Components/TableLoader";
import TableFoot from "../Components/TableFoot";
import DialogModal from "../Components/DialogModal";
import { useCallback, useEffect, useMemo, useState } from "react";
import AddNewNotification from "../Components/AddNewNotification";
import api from "../lib/api";
import useNotify from "../hooks/useNotify";
import useListData from "../hooks/useListData";
import useTableFilter from "../hooks/useTableFilter";
import useSortOptions from "../hooks/useSortOptions";

const EMPTY_FORM = { title: "", html: "", alert_type: "", users: [] };

// Recipients are stored as [{read, user}] but arrive either parsed or as text.
const toRecipientOptions = (value) => {
    let list = value;
    if (typeof value === "string") {
        try {
            list = JSON.parse(value);
        } catch {
            return [];
        }
    }
    if (!Array.isArray(list)) return [];
    return list.map((entry) => {
        const name = entry && typeof entry === "object" ? entry.user : entry;
        return { label: name, value: name };
    });
};

const Notifications = ({ setNotification }) => {
    const notify = useNotify(setNotification);
    const { filter, setFilter, clampPage } = useTableFilter({
        page: 1, limit: 10, keyword: "", sort_by: "id", sort_order: "DESC",
    });

    const [active, setActive] = useState("");
    const [dialog, setDialog] = useState({ title: "", text: "", show: false, id: "", loader: false });
    const [modals, setModals] = useState({ createNotification: false, deleteNotification: false });
    const [action, setAction] = useState("add");
    const [formData, setFormData] = useState(EMPTY_FORM);

    const sortBy = useSortOptions("accounts-sort-by", notify);
    const onListError = useCallback((err) => notify.error(err), [notify]);
    const { data: notifications, total, loading, reload } = useListData("/admin-access/notifications", filter, { onError: onListError });

    // Record count converted to a page count for the pager.
    const pageCount = useMemo(() => Math.max(1, Math.ceil(total / filter.limit)), [total, filter.limit]);
    useEffect(() => { clampPage(total); }, [total, clampPage]);

    const handlers = {
        addNewHandler() {
            // Without this reset, "Add New" after an edit reopened the modal
            // pre-filled and updated the previously edited record.
            setFormData(EMPTY_FORM);
            setAction("add");
            setModals((prev) => ({ ...prev, createNotification: true }));
        },
        openEditNotificationModal(item) {
            setActive("");
            setFormData({
                title: item.title,
                html: item.html,
                alert_type: item.alert_type,
                id: item.id,
                users: toRecipientOptions(item.users),
            });
            setAction("edit");
            setModals((prev) => ({ ...prev, createNotification: true }));
        },
        openDeleteNotificationModal(item) {
            setDialog({ show: true, title: "Delete notification", text: "Do you want to remove this notification?", id: item.id, loader: false });
            setActive("");
        },
        removeNotification() {
            setDialog((prev) => ({ ...prev, loader: true }));
            api.delete(`/notifications/${dialog.id}`)
                .then((res) => {
                    notify.success(res.data.message.en);
                    reload();
                })
                .catch((err) => notify.error(err))
                .finally(() => setDialog({ show: false, title: "", text: "", id: "", loader: false }));
        },
    };

    return (
        <div className="page-content-container">
            <h2 className="page-title">Notifications</h2>
            <TableHeader
                addNewHandler={handlers.addNewHandler}
                setNotification={setNotification}
                AddNewButtonIcon={SendPlusFill}
                setModals={setModals}
                filter={filter}
                sortBy={sortBy}
                exportButton={false}
                checkboxes={[]}
                setFilter={setFilter}
            />
            <div className="table-scroll">
                <table className="responsive-d-table">
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th>Message</th>
                            <th>Type</th>
                            <th><span className="sr-only">Actions</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {(!loading && notifications.length > 0) && notifications.map((item, id) => (
                            <tr key={item.id} className={(id + 2) % 2 === 0 ? "even-row" : "odd-row"}>
                                <td>{item.title}</td>
                                {/* Guarded against a null `html` column. */}
                                <td title={item.html || ""}>{(item.html || "").slice(0, 50)}</td>
                                <td><div className="alert-type-box" style={{ background: item.alert_type }} title={item.alert_type}></div></td>
                                <td>
                                    <button
                                        type="button"
                                        className="row-actions-trigger"
                                        aria-label={`Actions for ${item.title}`}
                                        onClick={() => setActive((prev) => (prev === item.id ? "" : item.id))}
                                    >
                                        <ThreeDotsVertical />
                                    </button>
                                    {active === item.id && <OutsideClickHandler onOutsideClick={() => setActive("")}>
                                        <div className="table-actions-box">
                                            <ul>
                                                <li onClick={() => handlers.openEditNotificationModal(item)}>Edit</li>
                                                <li onClick={() => handlers.openDeleteNotificationModal(item)}>Delete</li>
                                            </ul>
                                        </div>
                                    </OutsideClickHandler>}
                                </td>
                            </tr>
                        ))}
                        {(!loading && notifications.length === 0) && <tr className="empty-row">
                            <td colSpan={4}><div className="empty-records"><span>Records not found</span></div></td>
                        </tr>}
                        {loading && <TableLoader colspan={4} />}
                    </tbody>
                </table>
            </div>
            <TableFoot filter={filter} setFilter={setFilter} count={pageCount} total={total} />
            <DialogModal
                show={dialog.show}
                setShow={(value) => setDialog((prev) => ({ ...prev, show: value }))}
                title={dialog.title}
                text={dialog.text}
                onSubmit={handlers.removeNotification}
                loader={dialog.loader}
            />
            <AddNewNotification
                show={modals.createNotification}
                setShow={(value) => setModals((prev) => ({ ...prev, createNotification: value }))}
                formData={formData}
                action={action}
                setFormData={setFormData}
                setNotification={setNotification}
                onSaved={reload}
            />
        </div>
    )
}

export default Notifications
